import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';
import { reduxForm, Field, Fields, formValueSelector } from 'redux-form/immutable';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { Typography } from '@mui/material';

import { useInjectEpics } from 'utils/injectEpics';
import { useInjectReducer } from 'utils/injectReducer';
import { required, isEmail } from 'utils/validators';
import FormTextField from 'retired/FormTextField';
import SplashWrapper from 'retired/SplashWrapper';
import { selectUser } from 'entities/user/selectors';
import { useSsoCompany } from 'containers/LoginPage/useSsoCompany';
import { red } from 'utils/color-styles';

import { selectSubmitDone, selectErrorCode, selectSuccess } from './selectors';
import * as Actions from './actions';
import epics from './epics';
import reducer from './reducer';

const requiredValidator = required();
const isEmailValidator = isEmail();

const key = 'forgotPassword';

const RenderFields = ({ submitDone, userSsoCompany, ...fields }) => (
  <div>
    <Field
      name="email"
      type="email"
      label="Email"
      component={FormTextField}
      fullWidth
      autoFocus
      validate={[requiredValidator, isEmailValidator]}
      style={{ marginBottom: 20 }}
    />
    {userSsoCompany ? (
      <>
        <Typography color={red}>Your company only supports SSO as a login method</Typography>
        <Link to={`/login/sso/${userSsoCompany.id}`}>
          <Button color="primary" variant="contained" style={{ width: '100%', marginTop: 16 }}>
            Login with SSO
          </Button>
        </Link>
      </>
    ) : (
      <Button
        type="submit"
        color="primary"
        variant="contained"
        disabled={!(!submitDone && fields.email.meta.valid)}
        style={{ width: '100%', marginTop: 16 }}
      >
        Confirm
      </Button>
    )}
  </div>
);
RenderFields.propTypes = { submitDone: PropTypes.bool, userSsoCompany: PropTypes.object };

const valueSelector = formValueSelector(key);

const ForgotPassword = ({ actions, handleSubmit, submitDone, errorCode, success }) => {
  useInjectReducer({ key, reducer });
  useInjectEpics({ key, epics });
  useEffect(() => {
    actions.initAction();
  }, []);

  const emailFieldValue = useSelector((state) => valueSelector(state, 'email'));

  const userSsoCompany = useSsoCompany(emailFieldValue);

  const submit = (data) => actions.submitAction(data.get('email'));

  const errorMessages = {
    submitFailed: 'An error occurred.',
  };
  const successMessages = {
    default:
      "An email has been sent if an account exists matching the provided email. Click the link in the email to reset your password. If you don't see the email, check other places it might be, like your junk, spam, social, or other folders.",
  };

  return (
    <SplashWrapper>
      <Helmet title="Forgot Password" />
      <Typography variant="h1" mb={5}>
        Forgot your password?
      </Typography>
      <div style={{ fontSize: 16, marginBottom: 20 }}>
        {' '}
        Enter the email address you used to create your account.
      </div>
      <form onSubmit={handleSubmit(submit)}>
        <Fields
          names={['email']}
          component={RenderFields}
          submitDone={submitDone}
          userSsoCompany={userSsoCompany}
        />
        <Dialog open={submitDone}>
          <DialogTitle>{success ? 'Success' : 'Failed'}</DialogTitle>
          <DialogContent>
            {success ? successMessages.default : errorMessages[errorCode]}
          </DialogContent>
          <DialogActions>
            {success ? (
              <Link to="/login">
                <Button onClick={actions.resetDialogAction} color="primary" variant="contained">
                  Go Back
                </Button>
              </Link>
            ) : (
              <Button onClick={actions.initAction} color="primary" variant="contained">
                OK
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </form>
    </SplashWrapper>
  );
};

ForgotPassword.propTypes = {
  actions: PropTypes.object,
  handleSubmit: PropTypes.func,
  submitDone: PropTypes.bool,
  errorCode: PropTypes.string,
  success: PropTypes.bool,
};

const withConnect = connect(
  (state) => ({
    user: selectUser(state),
    submitDone: selectSubmitDone(state),
    errorCode: selectErrorCode(state),
    success: selectSuccess(state),
  }),
  (dispatch) => ({ actions: bindActionCreators(Actions, dispatch) })
);

const withForm = reduxForm({
  form: key,
  destroyOnUnmount: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
});

export default compose(withConnect, withForm)(ForgotPassword);
